import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  Phone,
  PhoneCall,
  Sparkles,
  ExternalLink,
  User,
  Building2,
} from 'lucide-react'

import {
  useFollowUp,
  useReassignFollowUp,
  useUpdateFollowUpStatus,
  type FollowUp,
} from '../api/followups'
import { useDirectory, type OrgExtension } from '../api/directory'
import { formatPhone, formatDateTime } from '../lib/utils'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { RecordingPlayer } from '../components/call-detail/RecordingPlayer'

export function FollowUpDetailPage() {
  const { t } = useTranslation('followups')
  const { followUpId } = useParams()
  const navigate = useNavigate()

  const { data, isLoading, isError, refetch } = useFollowUp(followUpId)
  const updateStatus = useUpdateFollowUpStatus()

  if (isLoading) return <SpinLoader label={t('detail.loading')} />

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorCard message={t('detail.notFound')} onRetry={() => refetch()} />
      </div>
    )
  }

  const fu = data.follow_up
  const isCompleted = fu.status === 'completed'

  const toggleStatus = async () => {
    if (!followUpId) return
    await updateStatus.mutateAsync({
      id: followUpId,
      status: isCompleted ? 'open' : 'completed',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <BackLink />
          <h1 className="text-xl font-bold text-text-primary md:text-2xl">
            {fu.requester_name || formatPhone(fu.requester_phone)}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fu.requester_phone && (
            <button
              onClick={() =>
                navigate(`/app/calls/stage/${encodeURIComponent(fu.requester_phone)}`)
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-success px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-success/80"
            >
              <PhoneCall className="h-4 w-4" />
              {t('detail.stageCall')}
            </button>
          )}
          <button
            onClick={toggleStatus}
            disabled={updateStatus.isPending}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
              isCompleted
                ? 'border border-border text-text-secondary hover:bg-surface-elevated'
                : 'bg-primary text-white hover:bg-primary/90'
            }`}
          >
            {isCompleted ? (
              <RotateCcw className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            {isCompleted ? t('detail.reopen') : t('detail.markComplete')}
          </button>
        </div>
      </div>

      {/* Requester + task */}
      <div className="rounded-lg border border-border bg-surface-card p-5 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            icon={User}
            label={t('detail.fields.requester')}
            value={fu.requester_name}
            placeholder={t('detail.placeholders.noName')}
          />
          <Field
            icon={Phone}
            label={t('detail.fields.phone')}
            value={fu.requester_phone ? formatPhone(fu.requester_phone) : ''}
            placeholder={t('detail.placeholders.noPhone')}
          />
        </div>
        <div>
          <p className="text-xs font-medium text-text-muted">{t('detail.fields.task')}</p>
          <p className="mt-0.5 text-sm text-text-primary">
            {fu.task || <span className="italic text-text-muted">{t('detail.placeholders.noTask')}</span>}
          </p>
        </div>
        {fu.ai_suggestion && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t('detail.fields.aiSuggestion')}
            </p>
            <p className="mt-1 text-sm text-text-secondary">{fu.ai_suggestion}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-4 text-xs text-text-muted">
          <span>{t('detail.meta.created', { value: formatDateTime(fu.created_at) })}</span>
          {fu.completed_at && (
            <span>{t('detail.meta.completed', { value: formatDateTime(fu.completed_at) })}</span>
          )}
        </div>
      </div>

      {/* Assignment + reassign */}
      <AssignmentCard followUpId={followUpId} fu={data.follow_up} />

      {/* Call context */}
      <CallContextCard
        callId={fu.call_id}
        recordingUrl={fu.recording_url}
        summary={fu.call_summary}
      />
    </div>
  )
}

function BackLink() {
  const { t } = useTranslation('followups')
  return (
    <Link
      to="/app/follow-ups"
      className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('detail.back')}
    </Link>
  )
}

function AssignmentCard({
  followUpId,
  fu,
}: {
  followUpId: string | undefined
  fu: FollowUp
}) {
  const { t } = useTranslation('followups')
  const { data: directory } = useDirectory()
  const reassign = useReassignFollowUp()
  const [editing, setEditing] = useState(false)
  const [selected, setSelected] = useState<string>(fu.assigned_extension_id ?? '')

  // Extensions grouped by location label for the reassign picker.
  const grouped = useMemo(() => {
    const exts = directory?.extensions ?? []
    const byLoc = new Map<string, OrgExtension[]>()
    for (const e of exts) {
      const key = e.location_label || t('detail.assignment.noLocation')
      const arr = byLoc.get(key) ?? []
      arr.push(e)
      byLoc.set(key, arr)
    }
    return Array.from(byLoc.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [directory?.extensions, t])

  const save = async () => {
    if (!followUpId) return
    await reassign.mutateAsync({
      id: followUpId,
      assigned_extension_id: selected || null,
    })
    setEditing(false)
  }

  const assignedLine = fu.assigned_extension_id
    ? [
        fu.assigned_extension_label,
        fu.assigned_location_label,
        fu.assigned_extension ? `ext ${fu.assigned_extension}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null

  return (
    <div className="rounded-lg border border-border bg-surface-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="flex items-center gap-1.5 text-lg font-semibold text-text-primary">
          <Building2 className="h-4 w-4 text-text-muted" />
          {t('detail.assignment.title')}
        </h2>
        {!editing && (
          <button
            onClick={() => {
              setSelected(fu.assigned_extension_id ?? '')
              setEditing(true)
            }}
            className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
          >
            {t('detail.assignment.reassign')}
          </button>
        )}
      </div>

      {!editing ? (
        <div className="mt-3 space-y-1">
          {assignedLine ? (
            <p className="text-sm text-text-primary">{assignedLine}</p>
          ) : (
            <p className="text-sm italic text-text-muted">
              {t('detail.assignment.unassigned')}
            </p>
          )}
          {fu.assigned_display_name && (
            <p className="text-xs text-text-muted">
              {t('detail.assignment.user', { name: fu.assigned_display_name })}
            </p>
          )}
          {fu.target_label && (
            <p className="text-xs text-text-muted">
              {t('detail.assignment.heard', { target: fu.target_label })}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('detail.assignment.unassignedOption')}</option>
            {grouped.map(([loc, exts]) => (
              <optgroup key={loc} label={loc}>
                {exts.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.extension} · {e.label || e.kind}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {reassign.isError && (
            <p className="text-xs text-error">{t('errors.reassignFailed')}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={reassign.isPending}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {reassign.isPending ? t('detail.assignment.saving') : t('detail.assignment.save')}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
            >
              {t('detail.assignment.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function CallContextCard({
  callId,
  recordingUrl,
  summary,
}: {
  callId: string | null
  recordingUrl: string | null
  summary: string | null
}) {
  const { t } = useTranslation('followups')

  if (!callId) {
    return (
      <div className="rounded-lg border border-border bg-surface-card p-5">
        <h2 className="text-lg font-semibold text-text-primary">
          {t('detail.call.title')}
        </h2>
        <p className="mt-2 text-sm italic text-text-muted">{t('detail.call.noCall')}</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-surface-card p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-text-primary">
          {t('detail.call.title')}
        </h2>
        <Link
          to={`/app/calls/${encodeURIComponent(callId)}`}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('detail.call.viewCall')}
        </Link>
      </div>

      {/* Recording: backfills only after the call is analyzed (call_analyzed). */}
      {recordingUrl ? (
        <RecordingPlayer callSid={callId} />
      ) : (
        <p className="rounded-lg bg-surface/60 px-4 py-3 text-xs text-text-muted">
          {t('detail.call.recordingPending')}
        </p>
      )}

      {summary && (
        <div>
          <p className="text-xs font-medium text-text-muted">{t('detail.call.summary')}</p>
          <p className="mt-1 whitespace-pre-line text-sm text-text-secondary">{summary}</p>
        </div>
      )}
    </div>
  )
}

function Field({
  icon: Icon,
  label,
  value,
  placeholder,
}: {
  icon?: React.ComponentType<{ className?: string }>
  label: string
  value: string
  placeholder?: string
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-text-primary">
        {Icon && <Icon className="h-3.5 w-3.5 text-text-muted" />}
        {value || <span className="italic text-text-muted">{placeholder}</span>}
      </p>
    </div>
  )
}
